import { useState, useRef, useCallback, DragEvent, ChangeEvent } from 'react';
import { Upload, X, AlertCircle, Loader2, Image as ImageIcon, Link } from 'lucide-react';
import { Input } from '@/frontend/components/ui/input';
import { Label } from '@/frontend/components/ui/label';
import { Progress } from '@/frontend/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/frontend/components/ui/tabs';
import { Button } from '@/frontend/components/ui/button';
import { cn } from '@/frontend/lib/utils';
import { uploadImage, validateImageFile } from '@/backend/services/storage/images';

export interface ImageUploadFieldProps {
  value: string;
  onChange: (url: string) => void;
  storagePath: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  showPreview?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
  /** When true, only URL input is shown (upload tab hidden) */
  uploadDisabled?: boolean;
  uploadDisabledMessage?: string;
}

export function ImageUploadField({
  value,
  onChange,
  storagePath,
  label,
  required = false,
  disabled = false,
  showPreview = true,
  onUploadingChange,
  uploadDisabled = false,
  uploadDisabledMessage,
}: ImageUploadFieldProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultTab = uploadDisabled ? 'url' : (value ? 'url' : 'upload');

  const setUploading = useCallback((uploading: boolean) => {
    setIsUploading(uploading);
    onUploadingChange?.(uploading);
  }, [onUploadingChange]);

  const handleFileUpload = async (file: File) => {
    setError('');

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploading(true);
      setIsCompressing(true);
      setUploadProgress(0);

      const result = await uploadImage({
        file,
        storagePath,
        onProgress: (progress) => {
          setIsCompressing(false);
          setUploadProgress(progress);
        },
      });

      setPreviewError(false);
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setIsCompressing(false);
      setUploadProgress(0);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload(files[0]);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) handleFileUpload(files[0]);
    // Reset so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBrowseClick = () => {
    if (!disabled && !isUploading) fileInputRef.current?.click();
  };

  const handleClear = () => {
    onChange('');
    setError('');
    setPreviewError(false);
  };

  const renderPreview = () => {
    if (!showPreview || !value) return null;
    return (
      <div className="mt-2 relative inline-block">
        {previewError ? (
          <div className="w-24 h-24 rounded border bg-muted flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          </div>
        ) : (
          <img
            src={value}
            alt="Preview"
            className="w-24 h-24 object-cover rounded border"
            onError={() => setPreviewError(true)}
          />
        )}
        {!disabled && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  };

  const renderUploadZone = () => {
    if (isCompressing) {
      return (
        <div className="border-2 border-primary rounded-lg p-6 bg-primary/5 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Compressing image...</p>
        </div>
      );
    }

    if (isUploading) {
      return (
        <div className="border-2 border-primary rounded-lg p-6 bg-primary/5">
          <div className="space-y-3">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-sm text-muted-foreground text-center">
              Uploading... {uploadProgress}%
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && 'border-border hover:border-primary/50 hover:bg-muted/50',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBrowseClick();
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || isUploading}
        />
        <div className="space-y-2">
          <div className="flex justify-center">
            <div className="rounded-full bg-primary/10 p-3">
              <Upload className="h-6 w-6 text-primary" />
            </div>
          </div>
          <p className="text-sm font-medium">
            Drag and drop an image, or click to browse
          </p>
          <p className="text-xs text-muted-foreground">
            JPEG, PNG, or WebP. Max 5 MB.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label>
          {label} {required && '*'}
        </Label>
      )}

      <Tabs defaultValue={defaultTab}>
        <TabsList className="w-full">
          <TabsTrigger value="url" className="flex-1 gap-1.5">
            <Link className="h-3.5 w-3.5" />
            URL
          </TabsTrigger>
          <TabsTrigger
            value="upload"
            className="flex-1 gap-1.5"
            disabled={uploadDisabled}
          >
            <Upload className="h-3.5 w-3.5" />
            Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="url">
          <Input
            type="url"
            value={value}
            onChange={(e) => {
              setPreviewError(false);
              onChange(e.target.value);
            }}
            placeholder="https://..."
            disabled={disabled}
          />
          {uploadDisabled && uploadDisabledMessage && (
            <p className="text-xs text-muted-foreground mt-1">
              {uploadDisabledMessage}
            </p>
          )}
          {renderPreview()}
        </TabsContent>

        <TabsContent value="upload">
          {renderUploadZone()}
          {renderPreview()}
        </TabsContent>
      </Tabs>

      {error && (
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
